import { FieldError, useForm } from "react-hook-form";
import AuditTrailProductSearchComponent from "../AuditTrailProductSearchComponent";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import { useEffect, useState } from "react";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useAuditTrailActionAPI } from "@/app/hooks/api_hooks/Inventory/AuditTrail/useAuditTrailActionAPI";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { IAuditTrailDataSummaryParamsBody } from "@/api_framework/api_modals/Inventory";
import { toast } from "react-hot-toast";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useDoctorListByClinicAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useDoctorListByClinicAPI";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setAuditTrailFilterSelection } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { AHS_CLICK_EVENTS, AHS_SCREEN_IDS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

const typeOptions = [
  { id: "CLINIC", title: "Clinic" },
  { id: "TAMS", title: "TAMS" },
  { id: "DOCTOR", title: "Doctor" },
];

export interface IInventoryPurchaseOrderDraftProductInfo {
  variant_name?: string;
  variant_id: string;
  in_hand_quantity: number;
  price_per_package: number | null;
  lot_number: string;
  manufacturer_name: string;
  mrp: number;
  expiry_date: string;
}

export interface IAuditTrailProps {
  clinic_tams: string;
  product_info: IInventoryPurchaseOrderDraftProductInfo[];
  city: string;
  clinics: Array<string>;
  doctors: Array<string>;
  action: Array<string>;
  productId: string;
  productName: string;
  type: "CLINIC" | "TAMS" | "DOCTOR";
}

const AuditTrailFiltersComponent = ({ setTableDataSummary }) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 50);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const [defaultActionsSet, setDefaultActionsSet] = useState(false);
  const { getRouteKey } = useRouter();
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startDay}-${startMonth}-${startYear}`;
  const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
  const endDateEpoch = Math.floor(new Date(endDate.setHours(23, 59, 59, 999)).getTime() / 1000);
  const routeId = getRouteKey("CONFIGURATION", "id");
  const { routeState: cityRouteState } = useRouteInfo(routeId);
  const { routeState: actionRouteState } = useRouteInfo(getRouteKey("INVENTORY", "id"));
  const { cityList } = cityRouteState.state as IConfigurationListState;
  const { auditTrailAction, auditTrailFilterSelection } = actionRouteState.state as IInventoryState;
  const dispatch = useAppDispatch();
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: fetchDoctors, doctorList } = useDoctorListByClinicAPI();
  /* Built manually (not via useSelectMenuReducer) so underscores in emails are not converted to spaces */
  const doctorItem = doctorList.map((d) => ({
    title: `${d.name} (${d.email})`,
    id: d.user_id,
    checkboxVisible: true,
    withSubItems: false,
    subSelectItems: [],
    state: {},
  }));
  const fetchCityList = useConfigurationCityListAPI();
  const cityListItem = useSelectMenuReducer(cityList, "name", "id");
  const actionItem = useSelectMenuReducer(auditTrailAction, "action_name", "action_id");
  const fetchActionList = useAuditTrailActionAPI();
  const { mappedClinicId, mappedClinicName, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const { logAhsEvent } = useAhsInventoryClickLog(AHS_SCREEN_IDS.AUDIT_TRAIL);

  const formHook = useForm<IAuditTrailProps>({
    mode: "onChange",
    defaultValues: {
      clinic_tams: "",
      product_info: [],
      type: auditTrailFilterSelection?.type ?? "CLINIC",
      city: mappedCityId,
      clinics: auditTrailFilterSelection?.clinics ?? [mappedClinicId],
      doctors: auditTrailFilterSelection?.doctors ?? [],
      action: [],
    },
  });

  useEffect(() => {
    fetchCityList();
    fetchActionList();
  }, []);

  useEffect(() => {
    if (auditTrailAction?.length && !defaultActionsSet) {
      const allActionIds = auditTrailAction.map((a) => a.action_id);
      formHook.setValue("action", allActionIds);
      setDefaultActionsSet(true);
    }
  }, [auditTrailAction, defaultActionsSet, formHook]);

  useEffect(() => {
    const city = formHook.watch("city");
    const type = formHook.watch("type");
    if (city && type) {
      const warehouseType = type === "TAMS" ? "tams" : "clinic";
      fetchClinicList(city, warehouseType);
    }
  }, [formHook.watch("city"), formHook.watch("type")]);

  useEffect(() => {
    const type = formHook.watch("type");
    const clinics = formHook.watch("clinics");
    if (type === "DOCTOR" && clinics?.length) {
      fetchDoctors(clinics, true);
    }
  }, [formHook.watch("type"), formHook.watch("clinics")]);

  useEffect(() => {
    dispatch(
      setAuditTrailFilterSelection({
        data: {
          type: formHook.watch("type"),
          clinics: formHook.watch("clinics"),
          doctors: formHook.watch("doctors"),
        },
      }),
    );
  }, [formHook.watch("type"), formHook.watch("clinics"), formHook.watch("doctors")]);

  const Submit = (data: IAuditTrailProps) => {
    if (!data?.productId) {
      toast.error("Please select a product before applying filters.");
      return;
    }
    if (data?.type === "DOCTOR" && !data?.doctors?.length) {
      toast.error("Please select a doctor.");
      return;
    }
    const filters: IAuditTrailDataSummaryParamsBody = {
      vetic_variant_id: data?.productId,
      clinic_ids: data?.clinics,
      doctor_ids: data?.type === "DOCTOR" ? data?.doctors : undefined,
      action_types: data?.action,
      start_date: Math.floor(startDate.getTime() / 1000),
      end_date: endDateEpoch,
    };
    logAhsEvent(AHS_CLICK_EVENTS.AUDIT_TRAIL_APPLY_FILTERS_CLICK, {
      search_item: data?.productName,
      filter_values: filters,
    });
    setTableDataSummary(filters);
  };

  return (
    <div className="bg-white p-2  rounded-xl">
      <div className="justify-start text-black text-base font-semibold tracking-wide">Inventory Audit Trail</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          Submit(formHook.getValues());
        }}
      >
        <div className="flex gap-2 sm:gap-4 items-center mt-3 flex-wrap">
          <div className="w-full sm:w-2/5">
            <AuditTrailProductSearchComponent
              onSelectItem={(item)=> {
                if (item) { 
                  formHook.setValue("productId", item.id);
                  formHook.setValue("productName", item.name);
                } else {
                  formHook.setValue("productId", null);
                  formHook.setValue("productName", null);
                }
              }}
              containerClassName="w-full shadow-none"
              inputContainerClassName="w-full"
              itemActiveClassName="bg-orange-500 text-white"
              flexibleAdd={false}
              searchType="Lab"
            />
          </div>
          <div className="w-full sm:w-[48%] lg:w-auto">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              handelClick={() => {
                // handelClick();
              }}
              containerClassName="w-full sm:w-[270px] mt-1"
              isFutureDateDisabled={true}
            />
          </div>
        </div>
        <div className="mt-5 flex justify-between flex-wrap">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <SearchableSelectMenu
              errorMessages={[{ message: null, type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("city", item.id);
                  formHook.setValue("clinics", []);
                }
              }}
              fieldError={formHook.formState.errors.city}
              register={formHook.register("city", { required: true })}
              selectItems={cityListItem}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={cityListItem?.find((oc) => oc.id === formHook.watch("city"))}
              listBoxClassName="w-[48%] sm:w-[200px]"
              className="text-gray-800 !leading-5"
              containerClassName=""
            />
            <SearchableSelectMenu
              errorMessages={[{ message: null, type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("type", item.id as "CLINIC" | "TAMS" | "DOCTOR");
                  formHook.setValue("clinics", []);
                  formHook.setValue("doctors", []);
                }
              }}
              fieldError={formHook.formState.errors.type}
              register={formHook.register("type")}
              selectItems={typeOptions}
              placeholder="Select Type"
              showTooltips={true}
              showDropdownIcon={true}
              defaultSelected={typeOptions?.find((t) => t.id === formHook.watch("type"))}
              disabled={!formHook.watch("city")}
              listBoxClassName="w-[48%] sm:w-[150px]"
              className="text-gray-800 !leading-5"
              containerClassName=""
            />
            <SearchableMultiSelectMenu
              onSelectItem={(items) => {
                const clinicId = items.map((i) => i.id);
                formHook.setValue("clinics", clinicId);
                formHook.clearErrors("clinics");
              }}
              errorMessages={[{ message: "", type: "required" }]}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics", {
                required: true,
              })}
              withSelectAll
              showTooltips={true}
              showTypedErrors={false}
              placeholder="Select Clinic"
              defaultSelected={clinicsOrgItem.filter((c) => formHook.watch("clinics")?.includes(c.id))}
              selectItems={clinicsOrgItem}
              containerClassName="w-full sm:w-[200px]"
              className="!leading-5"
              disabled={!formHook.watch("city")}
            />
            {formHook.watch("type") === "DOCTOR" && (
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const doctorIds = items.map((i) => i.id);
                  formHook.setValue("doctors", doctorIds);
                  formHook.clearErrors("doctors");
                }}
                errorMessages={[{ message: "", type: "required" }]}
                fieldError={formHook.formState.errors.doctors}
                register={formHook.register("doctors", {
                  required: true,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={false}
                placeholder="Select Doctor"
                defaultSelected={doctorItem.filter((d) => formHook.watch("doctors")?.includes(d.id))}
                selectItems={doctorItem}
                containerClassName="w-full sm:w-[200px]"
                className="!leading-5"
                disabled={!formHook.watch("clinics")?.length}
              />
            )}
            <SearchableMultiSelectMenu
              errorMessages={[{ type: "required", message: "Action is required" }]}
              onSelectItem={(items) => {
                const actionId = items.map((i) => i.id);
                formHook.setValue("action", actionId);
                formHook.clearErrors("action");
              }}
              fieldError={(formHook?.formState?.errors?.action as FieldError) ?? null}
              register={formHook.register("action", { required: true })}
              selectItems={actionItem}
              placeholder="Select Action"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={actionItem.filter((c) => formHook.watch("action")?.includes(c.id))}
              listBoxClassName="w-full"
              className="text-gray-800 !leading-5"
              containerClassName="sm:w-150px]"
              withSelectAll
            />

            <input
              type="submit"
              className="bg-black px-6 py-2 border-transparent rounded-lg text-xs font-medium text-white cursor-pointer"
              value={"Apply Filters"}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AuditTrailFiltersComponent;
